import { getAuthHeaders, resolveEndpoint } from './api'

export type Plan = 'starter' | 'professional' | 'premium'
export type Interval = 'month' | 'year'

function resolveCheckoutEndpoint(): string {
  return resolveEndpoint('/billing/checkout')
}

function resolveBillingEndpoint(path: string): string {
  return resolveEndpoint(`/billing${path}`)
}

export async function beginSubscriptionCheckout(params: {
  plan: Plan
  interval: Interval
  email?: string
  userId?: string
  signal?: AbortSignal
}): Promise<void> {
  const endpoint = resolveCheckoutEndpoint()
  const authHeaders = params.userId ? await getAuthHeaders() : {}
  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      plan: params.plan,
      interval: params.interval,
      email: params.email,
      userId: params.userId,
    }),
    signal: params.signal,
  })
  
  let data: { url?: string; error?: string }
  try {
    data = (await resp.json()) as { url?: string; error?: string }
  } catch {
    throw new Error(`Checkout failed (${resp.status}): Unable to parse response`)
  }
  
  if (!resp.ok) {
    const msg = data.error || `Checkout failed (${resp.status})`
    throw new Error(msg)
  }
  
  if (data.error) {
    throw new Error(data.error)
  }
  
  if (!data?.url) {
    throw new Error('Missing checkout URL')
  }
  
  window.location.href = data.url
}

/**
 * Creates a Stripe customer portal session for subscription management
 * @param userId - The user ID
 * @param returnUrl - URL to return to after portal session (defaults to current page)
 * @returns Portal URL or throws error
 */
export async function createCustomerPortalSession(
  userId: string,
  returnUrl?: string
): Promise<string> {
  const endpoint = resolveBillingEndpoint('/portal')
  const authHeaders = await getAuthHeaders()
  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      userId,
      returnUrl: returnUrl || window.location.href,
    }),
  })

  let data: { url?: string; error?: string }
  try {
    data = (await resp.json()) as { url?: string; error?: string }
  } catch {
    throw new Error(`Portal session failed (${resp.status}): Unable to parse response`)
  }

  if (!resp.ok) {
    const msg = data.error || `Portal session failed (${resp.status})`
    throw new Error(msg)
  }

  if (data.error) {
    throw new Error(data.error)
  }

  if (!data?.url) {
    throw new Error('Missing portal URL')
  }

  return data.url
}

/**
 * Cancels a subscription (sets cancel_at_period_end to true)
 * @param userId - The user ID
 * @param subscriptionId - The subscription ID to cancel
 * @returns Success status or throws error
 */
export async function cancelSubscription(
  userId: string,
  subscriptionId: string
): Promise<void> {
  const endpoint = resolveBillingEndpoint('/cancel')
  const authHeaders = await getAuthHeaders()
  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      userId,
      subscriptionId,
    }),
  })

  let data: { error?: string; success?: boolean }
  try {
    data = (await resp.json()) as { error?: string; success?: boolean }
  } catch {
    throw new Error(`Cancel subscription failed (${resp.status}): Unable to parse response`)
  }

  if (!resp.ok) {
    const msg = data.error || `Cancel subscription failed (${resp.status})`
    throw new Error(msg)
  }

  if (data.error) {
    throw new Error(data.error)
  }
}

/**
 * Resumes a cancelled subscription (sets cancel_at_period_end to false)
 * @param userId - The user ID
 * @param subscriptionId - The subscription ID to resume
 * @returns Success status or throws error
 */
export async function resumeSubscription(
  userId: string,
  subscriptionId: string
): Promise<void> {
  const endpoint = resolveBillingEndpoint('/resume')
  const authHeaders = await getAuthHeaders()
  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      userId,
      subscriptionId,
    }),
  })

  let data: { error?: string; success?: boolean }
  try {
    data = (await resp.json()) as { error?: string; success?: boolean }
  } catch {
    throw new Error(`Resume subscription failed (${resp.status}): Unable to parse response`)
  }

  if (!resp.ok) {
    const msg = data.error || `Resume subscription failed (${resp.status})`
    throw new Error(msg)
  }

  if (data.error) {
    throw new Error(data.error)
  }
}

export interface UpgradeResult {
  success: boolean
  error?: string
  code?: string
  subscriptionId?: string
  carryOverCredits?: number
}

/**
 * Upgrades a subscription to a new plan
 *
 * The upgrade flow:
 * 1. Charges full price for new plan immediately
 * 2. Cancels old subscription immediately (no refund)
 * 3. Carries over remaining credits from old plan to new plan
 *
 * @param userId - The user ID
 * @param subscriptionId - The subscription ID to upgrade
 * @param newPlan - The new plan slug (starter, professional, premium)
 * @param interval - The billing interval (month or year)
 * @returns Result object with success status and carryover credits
 */
export async function upgradeSubscription(
  userId: string,
  subscriptionId: string,
  newPlan: Plan,
  interval: Interval
): Promise<UpgradeResult> {
  const endpoint = resolveBillingEndpoint('/upgrade')
  const authHeaders = await getAuthHeaders()
  const resp = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({
      userId,
      subscriptionId,
      plan: newPlan,
      interval,
    }),
  })

  let data: {
    error?: string
    code?: string
    success?: boolean
    subscriptionId?: string
    carryOverCredits?: number
  }
  try {
    data = await resp.json()
  } catch {
    return {
      success: false,
      error: `Upgrade subscription failed (${resp.status}): Unable to parse response`,
    }
  }

  if (!resp.ok || data.error) {
    return {
      success: false,
      error: data.error || `Upgrade subscription failed (${resp.status})`,
      code: data.code,
    }
  }

  return {
    success: true,
    subscriptionId: data.subscriptionId,
    carryOverCredits: data.carryOverCredits,
  }
}


